import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Container, Col, Row } from "react-bootstrap";
import ExamHeader from "../../../components/examComponents/ExamHeader";
import ExamOptions from "../../../components/examComponents/ExamOptions";
import DividerLine from "../../../components/examComponents/DividerLine";
import PreviewStatement from "../../../components/examComponents/PreviewStatement";
import ReviewScreen from "../../../components/examComponents/ReviewScreen";
import BreakScreen from "../../../components/examComponents/BreakScreen";
import Footer from "../../../components/examComponents/FooterNavigation";
import Calculator from "../../../components/examComponents/Calculator";
import NumericalInstructions from "../../../components/examComponents/NumericalInstructions";
import AnnotationModal from "../../../components/examComponents/AnnotationModal";
import {
  useFetchExamData,
  calculateAdaptiveSATScore,
  calculateAdaptiveMathSATScore,
  calculateCombinedSATScore,
} from "../../../utils/examFunctions";
import {
  createExamAttempt,
  updateExamAttempt,
} from "../../../utils/assessments";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setExamData } from "../../../store/examSlice";
import "./style.scss";
import Loader from "../../../components/Loader";
import { useSelector } from "react-redux";
import {
  clearFullLengthExamAll,
  setAttemptedQuestions,
  setCurrentQuestionIndex,
  setCurrentSectionIndex,
  setExamResults,
  setMarkedQuestions,
  setReviewMode,
  setSectionCorrectCounts,
  setSessionLogs,
  setShowBreakScreen,
  setShowEliminateButtons,
  setStartTime,
  setTime,
  setTimeLeft,
  setUserAnswers,
} from "../../../store/fullLengthExam";
import SaveAndExitDialog from "./SaveAndExitDialog";
import { getUserRole } from "../../../store/userRole";
import WaitModule from "./WaitModule";
import SectionLoader from "./SectionLoader";
import Reference from "../../../components/examComponents/Reference";
import SplitPane, { Pane } from "split-pane-react";
import "split-pane-react/esm/themes/default.css";
import {clearExamDataForcefully} from "./clearExamData";

function Exams() {
  const dispatch = useDispatch();
  const previewStatementRef = useRef(null);
  const navigate = useNavigate();
  const { id: examId, classroomId } = useParams();
  const roleOfTheUser = useSelector(getUserRole);
  const [highlights, setHighlights] = useState([]);
  const [failedAttempt, setFailAttempt] = useState(false)
  const contentRef = useRef(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)

  const fullLengthExam = useSelector((state) => state.fullLengthExam);
  const {
    currentSectionIndex = 0,
    currentQuestionIndex = 0,
    userAnswers = [],
    attemptedQuestions = [],
    reviewMode = false,
    markedQuestions,
    showBreakScreen = false,
    examResults = {},
    sectionCorrectCounts = [],
    startTime,
    isSavedExam = false,
    savedExamId = "",
    time,
    sessionLogs = [],
  } = fullLengthExam;

  const [sizes, setSizes] = useState(["50%", "50%"]);

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [eliminatedOptions, setEliminatedOptions] = useState([]);
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [isloading, setLoading] = useState(false);
  const [waitModule, setWaitModule] = useState(false);
  const [sectionLoader, setSectionLoader] = useState(false);
  const [openSaveAndExist, setOpenSaveAndExist] = useState(false);
  const [singleHighlight, setSingleHighlight] = useState({});

  const { examData, loading, error, setExamQuestions, isDataFetched } =
    useFetchExamData(examId);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showReference, setShowReference] = useState(false);
  const [numericAnswer, setNumericAnswer] = useState("");

  const hiddenButton = useRef(null);

  useEffect(() => {
    if (examData && isDataFetched) {
      // Fetch the exam data and dispatch it to the store
      dispatch(
        setUserAnswers(
          examData.sections.map((section) =>
            Array(section.questions?.length || 0).fill(null),
          ),
        ),
      );
    }
  }, [dispatch, examData, isDataFetched]);

  useEffect(() => {
    if (
      examData?.sections?.length &&
      userAnswers?.left &&
      examData?.sections[currentSectionIndex] &&
      examData?.sections[currentSectionIndex]?.questions?.length &&
      examData?.sections[currentSectionIndex]?.questions?.length !==
        userAnswers[currentSectionIndex]?.length
    ) {
      // Fetch the exam data and dispatch it to the store
      const tempUserAnswers = structuredClone(userAnswers);
      userAnswers[currentSectionIndex] = Array(
        examData?.sections[currentSectionIndex]?.questions?.length || 0,
      ).fill(null);
      dispatch(setUserAnswers(tempUserAnswers));
    }
  }, [currentSectionIndex, dispatch, examData, userAnswers]);

  useEffect(() => {
    if (examData) {
      dispatch(
        setExamResults({
          name: examData?.name,
          subjectName: "Reading",
          examId: examData?._id,
          classroomId: classroomId,
          examType: "assessment",
          totalQuestions: examData?.totalQuestions,
          correct: 0,
          answeredQuestions: [],
          timeSpent: 0,
          totalPoints: examData?.totalPoints,
          obtainedPoints: 0,
          duration: 30,
          concepts: [],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examData?._id]);

  const currentSectionQuestions =
    examData?.sections[currentSectionIndex]?.questions;

  const getSelectionCharacterOffsetWithin = useCallback((element) => {
    const selection = window.getSelection();
    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;
    const anchorOffset = selection.anchorOffset;
    const focusOffset = selection.focusOffset;
    let start = 0;
    let end = 0;

    const elementText = element.textContent;
    const anchorNodeIndex =
      anchorNode && elementText.indexOf(anchorNode.textContent);
    const focusNodeIndex =
      focusNode && elementText.indexOf(focusNode.textContent);

    if (anchorNode === focusNode) {
      start = Math.min(
        anchorNodeIndex + anchorOffset,
        focusNodeIndex + focusOffset,
      );
      end = Math.max(
        anchorNodeIndex + anchorOffset,
        focusNodeIndex + focusOffset,
      );
    } else {
      start = anchorNodeIndex + anchorOffset;
      end = focusNodeIndex + focusOffset;
    }

    return { start, end };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const timeChange = (prevTime) => {
        return prevTime + 1;
      };
      if(!failedAttempt) {
        dispatch(setStartTime(timeChange(startTime)));
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [startTime, dispatch, failedAttempt]);

  useEffect(() => {
    if (examData?.sections) {
      const secCorrectCounts = examData?.sections?.map((_, index) => {
        const section = attemptedQuestions?.[index];
        if (section && Array.isArray(section)) {
          return section.reduce(
            (count, question) => count + (question?.isCorrect ? 1 : 0),
            0,
          );
        } else {
          return 0; // Default to 0 if the section is skipped
        }
      });
      dispatch(setSectionCorrectCounts(secCorrectCounts));
    }
  }, [attemptedQuestions, examData?.sections, dispatch]);

  //  useEffect(() => {
  //   const readingWritingScore = calculateAdaptiveSATScore(27,27); // Example correct answers for two sections
  //   const mathScore = calculateAdaptiveMathSATScore(22, 22); // Example correct answers for two math sections
  //   const combinedScore = calculateCombinedSATScore(
  //     readingWritingScore,
  //     mathScore
  //   );
  //   console.log(`Reading/Writing Score: ${readingWritingScore}`);
  //   console.log(`Math Score: ${mathScore}`);
  //   console.log(`Combined SAT Score: ${combinedScore}`);
  //  }, []);

  // Adjust the annotations handling:
  const handleNewAnnotation = (text, note, color) => {
    setHighlights([
      ...highlights,
      { ...singleHighlight, color, tooltip: note },
    ]);
    setSelectedText("");
    setSingleHighlight({});
  };

  const handleFinishExam = async () => {
    const secCorrectCounts = examData?.sections?.map((_, index) => {
      const section = attemptedQuestions?.[index];
      if (section && Array.isArray(section) && section.length > 0) {
        return section?.reduce(
          (count, question) => count + (question?.isCorrect ? 1 : 0),
          0,
        );
      } else {
        return 0; // Handle empty sections
      }
    });
    if(!failedAttempt) {
      // Update exam results with time and prepare for submission
      const updatedExamResults = (prevResults) => ({
        ...prevResults,
        timeSpent: startTime, // Time spent in seconds
        secCorrectCounts, // Update the exam results with the correct counts
      });
      dispatch(setExamResults(updatedExamResults(examResults)));
    }

    // Prepare the data for backend submission
    try {
      setWaitModule(true);
      // Calculate SAT scores if applicable

      const readingScore = secCorrectCounts[0]; // Example index for Reading/Writing
      const writingScore = secCorrectCounts[1]; // Example index for Math

      const satReadingWritingScore = calculateAdaptiveSATScore(
        readingScore,
        writingScore,
      );
      const satMathScore = calculateAdaptiveMathSATScore(
        secCorrectCounts[2],
        secCorrectCounts[3],
      ); // Adjusted to just math score
      const combinedSATScore = calculateCombinedSATScore(
        satReadingWritingScore,
        satMathScore,
      );

      // Final results with SAT scores
      const finalResults = {
        ...examResults,
        attemptedQuestions,
        readingWritingScore: satReadingWritingScore,
        mathScore: satMathScore,
        combinedSATScore,
      };
      const preparedAnsweredQuestions = attemptedQuestions?.flat() ?? []
      const resultData = JSON.stringify(finalResults);
      const timeSpent = startTime;
      const updatedSessionLogs  = [
        ...sessionLogs,
            {
              moduleName: `${examData?.sections[currentSectionIndex]?.name} got ended.`,
              endedTime: new Date(),
            },
            {
              moduleName: `Session got ended.`,
              endedTime: new Date(),
            },
      ]
      dispatch(setSessionLogs(updatedSessionLogs))
      const jsonString = JSON.stringify({
        ...examResults,
        secCorrectCounts, // Ensure this is part of the data sent to the backend
        url: resultData,
        answeredQuestions: preparedAnsweredQuestions,
        timeSpent: timeSpent ? timeSpent : 345, // Time spent in seconds
        data: JSON.stringify({
          ...examData,
          markedQuestions: markedQuestions,
          sessionLogs: !failedAttempt ? updatedSessionLogs : sessionLogs,
        }),
        courseId: examData?.courseId,
        classroomId: classroomId,
        inProgress: "false",
      });
      let attemptId = "";
      if (isSavedExam && savedExamId) {
        const attempt = await updateExamAttempt(jsonString, savedExamId, {
          toastText: "Exam Submitted Successfully",
        });
        if (attempt?.examAttempt?.updatedAt) {
          const jsonData = JSON?.parse(jsonString);
          dispatch(
            setExamData({
              ...JSON?.parse(jsonData?.data),
              urlData: JSON?.parse(jsonData?.url),
              updatedAt: attempt?.examAttempt?.updatedAt,
            }),
          );
        }
        attemptId = attempt?.examAttempt?._id ?? "";
      } else {
        const attempt = await createExamAttempt(jsonString, classroomId, {
          toastText: "Exam Submitted Successfully",
        });
        if (attempt?.examAttempt?.updatedAt) {
          const jsonData = JSON?.parse(jsonString);
          dispatch(
            setExamData({
              ...JSON?.parse(jsonData?.data),
              urlData: JSON?.parse(jsonData?.url),
              updatedAt: attempt?.examAttempt?.updatedAt,
            }),
          );
        }
        attemptId = attempt?.examAttempt?._id ?? "";
      }
      if(attemptId?.length) {
        dispatch(clearFullLengthExamAll());
        dispatch(setExamResults({}));
        clearExamDataForcefully(dispatch);
        // Navigate to result page
        let navigatedPath = `result?attemptId=${attemptId}`
        if(roleOfTheUser !== 'Student') {
          navigatedPath += '&isAttempted=true'
        }
        navigate(navigatedPath);
      }
      setWaitModule(false);
      setFailAttempt(true);
      setIsErrorModalOpen(true)
    } catch (err) {
      setWaitModule(false);
      setFailAttempt(true)
      setIsErrorModalOpen(true)
      console.error(err);
    }
  };
  if (loading) return <Loader />;
  if (error) return <div>Error: {error}</div>;
  if (!examData) return <div>No exam data available</div>;
  if (waitModule) {
    return <WaitModule />;
  }
  if (sectionLoader) {
    return <SectionLoader />;
  }
  const handleMouseUp = () => {
    const selection = window.getSelection();
    const selectedText = selection?.toString();
    if (!selectedText || selectedText.trim() === "") return;
    const fullText = contentRef.current?.innerText || "";
    const start = fullText.indexOf(selectedText);
    const end = start + selectedText.length;
    setSelectedText(selectedText);

    if (start < 0 || end <= start) return;
    setSingleHighlight({
      start,
      end,
    });
  };

  // Function to handle the click event of the "Annotate" button
  const handleAnnotateClick = () => {
    if (singleHighlight?.end) {
      setShowAnnotationModal(true);
    } else {
      alert("No text selected for annotation.");
    }
  };

  const handleAnswerSelect = (question, answer, isCorrect) => {
    // Update user answers
    let questionId = question._id;
    const updateUserAnswers = (prevAnswers) => {
      const updatedAnswers = structuredClone(prevAnswers);
      const currentSectionAnswers = updatedAnswers[currentSectionIndex] || [];
      currentSectionAnswers[currentQuestionIndex] = { questionId, answer }; // Ensures the specific question index is updated
      updatedAnswers[currentSectionIndex] = currentSectionAnswers;
      return updatedAnswers;
    };
    dispatch(setUserAnswers(updateUserAnswers(userAnswers)));

    // Update attempted questions with the new answer
    const updateAttemptedQuestions = (prevAttempted) => {
      const updatedAttempted = structuredClone(prevAttempted);
      const sectionAttempts = updatedAttempted[currentSectionIndex]
        ? updatedAttempted[currentSectionIndex]
        : [];
      const existingAttemptIndex = sectionAttempts.findIndex(
        (a) => a._id === questionId,
      );

      if (existingAttemptIndex >= 0) {
        // Update existing attempt
        sectionAttempts[existingAttemptIndex] = {
          ...sectionAttempts[existingAttemptIndex],
          answer,
          isCorrect,
        };
      } else {
        // Add new attempt if not found
        sectionAttempts.push({
          ...question,
          answer, // Ensure to add answer here
          isCorrect,
        });
      }

      updatedAttempted[currentSectionIndex] = sectionAttempts;
      return updatedAttempted;
    };

    dispatch(
      setAttemptedQuestions(updateAttemptedQuestions(attemptedQuestions)),
    );

    // Update exam results
    const updatedExamResults = (prevResults) => {
      let answerIndex = prevResults.answeredQuestions.findIndex(
        (aq) => aq._id === questionId, // Ensure you are using the correct unique identifier
      );
      // Clone the answeredQuestions to ensure immutability
      let updatedAnsweredQuestions = [...prevResults.answeredQuestions];

      // If this question has been answered before, update it
      if (answerIndex >= 0) {
        updatedAnsweredQuestions[answerIndex] = {
          ...updatedAnsweredQuestions[answerIndex],
          answer,
          isCorrect,
        };
      } else {
        // Add new answer
        updatedAnsweredQuestions.push({
          ...question,
          answer,
          isCorrect,
        });
        answerIndex = updatedAnsweredQuestions.findIndex(
          (aq) => aq._id === questionId, // Ensure you are using the correct unique identifier
        );
      }

      // Calculate correct count adjustments based on the change
      let correctAdjustment = 0;
      if (isCorrect && !updatedAnsweredQuestions[answerIndex]?.isCorrect)
        correctAdjustment = 1;
      if (!isCorrect && updatedAnsweredQuestions[answerIndex]?.isCorrect)
        correctAdjustment = -1;

      return {
        ...prevResults,
        answeredQuestions: [...updatedAnsweredQuestions],
        correct: prevResults.correct + correctAdjustment,
        obtainedPoints: prevResults.obtainedPoints + correctAdjustment, // Assuming each question is worth 1 point
      };
    };
    dispatch(setExamResults(updatedExamResults(examResults)));
  };

  const handleSaveAndExitHandler = async () => {
    try {
      const payload = {
        name: examData?.name,
        subjectName: "Reading",
        examId: examData?._id,
        classroomId: classroomId,
        examType: "assessment",
        inProgress: "true",
        data: JSON.stringify(fullLengthExam),
        courseId: examData?.courseId,
      };

      setLoading(true);
      if (isSavedExam && savedExamId) {
        await updateExamAttempt(JSON.stringify(payload), savedExamId, {
          toastText: "Exam saved and exit Successfully",
        });
      } else {
        await createExamAttempt(JSON.stringify(payload), classroomId, {
          toastText: "Exam saved and exit Successfully",
        });
      }
      dispatch(clearFullLengthExamAll());
      // Navigate to result page
      setLoading(false);
      navigate(
        `${
          roleOfTheUser === "Student" ? "/student" : ""
        }/myClassrooms/${classroomId}/full-length-exams`,
      );
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const handleSectionCompletion = () => {
    const currentCorrectCount = sectionCorrectCounts[currentSectionIndex] || 0;
    setSelectedText("");
    setHighlights([]);
    if (currentSectionIndex === 0 || currentSectionIndex === 2) {
      setSectionLoader(true);
      const nextSectionIndex = currentSectionIndex + 1;
      const nextSectionType = examData?.sections[nextSectionIndex]?.type;

      if (nextSectionType === "Random" || nextSectionType === "Adaptive") {
        const newQuestions = getQuestionsForAdaptiveModule(
          currentCorrectCount,
          examData.sections[nextSectionIndex].rule,
        );
        updateQuestionsForNextModule(
          nextSectionIndex,
          newQuestions?.questions || [],
        );
      }
      const sessionLogsData = [...sessionLogs];
      sessionLogsData.push({
        moduleName: `${examData?.sections[currentSectionIndex]?.name} got ended.`,
        endedTime: new Date(),
      });

      dispatch(setReviewMode(false));
      setReviewModalOpen(false);
      setTimeout(() => {
        sessionLogsData.push({
          moduleName: `${
            examData?.sections[currentSectionIndex + 1]?.name
          } got started.`,
          endedTime: new Date(),
        });
        dispatch(setSessionLogs(sessionLogsData));
        dispatch(setCurrentSectionIndex(nextSectionIndex));
        dispatch(setShowEliminateButtons(false));
        dispatch(setTime(examData?.sections[nextSectionIndex]?.duration * 60));
        setSectionLoader(false);
      }, 3000);
    } else if (currentSectionIndex === 1) {
      dispatch(
        setSessionLogs([
          ...sessionLogs,
          {
            moduleName: `${examData?.sections[currentSectionIndex]?.name} got ended.`,
            endedTime: new Date(),
          },
        ]),
      );
      dispatch(setTimeLeft(600));
      dispatch(setShowBreakScreen(true));
    } else {
      goToNextSection(true);
    }
  };

  // based on the user's performance and the rule defined for adaptive/random modules.
  function getQuestionsForAdaptiveModule(correctCount, rule) {
    // Iterate through conditions to check if any matches the correctCount
    for (const condition of rule.conditions) {
      const [min, max] = condition.range.split(",").map(Number);
      // console.log(correctCount, min, max);
      if (correctCount >= min && correctCount <= max) {
        return condition;
      }
    }

    // Fallback: Find and return questions from the default condition
    const defaultCondition = rule.conditions.find(
      (condition) => condition.isDefault,
    );

    if (defaultCondition) {
      return defaultCondition;
    }

    console.warn("No matching range or default condition found.");
    return []; // In case there's no default condition
  }

  function updateQuestionsForNextModule(sectionIndex, newQuestions) {
    setExamQuestions(sectionIndex, newQuestions);
    dispatch(setCurrentQuestionIndex(0)); // Reset to the first question of the new section
  }

  const goToNextSection = (isBreakReturn = false) => {
    if (currentSectionIndex < examData.sections.length - 1) {
      setSectionLoader(true);
      const sessionLogsData = [...sessionLogs];
      if (isBreakReturn) {
        sessionLogsData.push({
          moduleName: `${examData?.sections[currentSectionIndex]?.name} got ended.`,
          endedTime: new Date(),
        });
      }
      dispatch(setReviewMode(false));
      setReviewModalOpen(false);
      setTimeout(() => {
        sessionLogsData.push({
          moduleName: `${
            examData?.sections[currentSectionIndex + 1]?.name
          } got started.`,
          endedTime: new Date(),
        });
        dispatch(setSessionLogs(sessionLogsData));
        dispatch(
          setTime(examData?.sections[currentSectionIndex + 1].duration * 60),
        );
        dispatch(setCurrentQuestionIndex(0));
        dispatch(setCurrentSectionIndex(currentSectionIndex + 1));
        dispatch(setShowEliminateButtons(false));
        setSelectedText("");
        setHighlights([]);
        setSectionLoader(false);
      }, 3000);
    } else {
      // Handle the last section case here
      handleFinishExam();
    }
  };

  const onBreakEnd = () => {
    dispatch(setTimeLeft(600));
    dispatch(setShowBreakScreen(false));
    goToNextSection(false); // Move to Section 2, Module 1
  };

  // Include this in your component render logic
  if (showBreakScreen) {
    return <BreakScreen onBreakEnd={onBreakEnd} />;
  }

  const goToNextQuestion = (
    e,
    lastQuestion = false,
    tempReviewModal = false,
  ) => {
    const isLastQuestionInSection =
      lastQuestion ||
      currentQuestionIndex ===
        examData.sections[currentSectionIndex].questions.length - 1;

    // If it's the last question of section 0 (first module) and in review mode
    if (
      isLastQuestionInSection &&
      (currentSectionIndex === 0 || currentSectionIndex === 2) &&
      (reviewMode || tempReviewModal)
    ) {
      handleSectionCompletion();
    }
    // If it's the last question of section 1 (second module), complete the section
    else if (
      isLastQuestionInSection &&
      currentSectionIndex === 1 &&
      (reviewMode || tempReviewModal)
    ) {
      handleSectionCompletion();
    }
    // If it's the last question and we are reviewing, go to the next section
    else if (isLastQuestionInSection && (reviewMode || tempReviewModal)) {
      goToNextSectionFromReview(); // Go to the next section from the review page
    }
    // If it's the last question but not in review mode, switch to review mode
    else if (isLastQuestionInSection) {
      dispatch(setReviewMode(true));
    }
    // If not the last question, go to the next question
    else {
      dispatch(setCurrentQuestionIndex(currentQuestionIndex + 1));
      setSelectedText("");
      setHighlights([]);
      setNumericAnswer("");
    }
  };

  // Implement the goToNextSectionFromReview function
  const goToNextSectionFromReview = () => {
    if (currentSectionIndex < examData.sections.length - 1) {
      setSectionLoader(true);
      dispatch(setReviewMode(false));
      setReviewModalOpen(false); // If you have a review modal, close it
      const sessionLogsData = [...sessionLogs];
      sessionLogsData.push({
        moduleName: `${examData?.sections[currentSectionIndex]?.name} got ended.`,
        endedTime: new Date(),
      });
      setTimeout(() => {
        sessionLogsData.push({
          moduleName: `${
            examData?.sections[currentSectionIndex + 1]?.name
          } got started.`,
          endedTime: new Date(),
        });
        dispatch(setSessionLogs(sessionLogsData));
        dispatch(
          setTime(examData?.sections[currentSectionIndex + 1].duration * 60),
        );
        dispatch(setCurrentQuestionIndex(0));

        dispatch(setCurrentSectionIndex(currentSectionIndex + 1));
        dispatch(setShowEliminateButtons(false));
        setSectionLoader(false);
      }, 3000);
    } else {
      handleFinishExam();
      // Handle the case where it's the last section (e.g., show final review or submission screen)
    }
  };

  const goToPreviousQuestion = () => {
    if(failedAttempt) {
      return
    }
    if (currentQuestionIndex > 0) {
      dispatch(setCurrentQuestionIndex(currentQuestionIndex - 1));
    }
  };
  const toggleMarkQuestion = (questionId) => {
    const updateMarkedQuestions = (current) => {
      if (current.includes(questionId)) {
        // Unmark the question if it's already marked
        return current.filter((id) => id !== questionId);
      } else {
        // Mark the question if it's not already marked
        return [...current, questionId];
      }
    };
    dispatch(setMarkedQuestions(updateMarkedQuestions(markedQuestions)));
  };

  const openReviewModal = () => {
    setReviewModalOpen(true);
  };

  const onSaveAndExitClick = () => {
    if(failedAttempt) {
      return
    }
    setOpenSaveAndExist(true);
  };

  const closeReviewModal = () => {
    setReviewModalOpen(false);
  };

  const handleOptionEliminate = (option) => {
    setEliminatedOptions((current) => {
      if (current.includes(option)) {
        // Option is already eliminated, allow it to be re-enabled
        return current.filter((el) => el !== option);
      } else {
        // Eliminate the option
        return [...current, option];
      }
    });
  };

  const resetEliminatedOptions = () => {
    setEliminatedOptions([]);
  };

  const toggleCalculator = () => {
    if(failedAttempt) {
      return
    }
    setShowCalculator(!showCalculator);
  };
  const toggleRefernce = () => {
    if(failedAttempt) {
      return
    }
    setShowReference(!showReference);
  };

  const selectedOption =
    userAnswers[currentSectionIndex] &&
    userAnswers[currentSectionIndex][currentQuestionIndex]
      ? userAnswers[currentSectionIndex][currentQuestionIndex]?.answer
      : "";

  const renderQuestionScreen = () => {
    const currentQuestion =
      examData?.sections[currentSectionIndex]?.questions[currentQuestionIndex];
    const isSecondHalf = currentSectionIndex >= 2; // Change this if you have more sections
    const containerStyle = {
      margin: "0 auto", // Center the container horizontally
    };

    if (isSecondHalf) {
      // Layout for sections 2 and higher
      return (
        <Container fluid style={containerStyle}>
          <Row className="mt-4">
            {currentQuestion?.type === "numerical" ? (
              <>
                <div
                  xs={6}
                  style={{
                    padding: 0,
                    display: "flex",
                    position: "fixed",
                    top: "82px",
                    background: "white",
                    width: "50%",
                  }}
                >
                  <div
                    style={{ width: "100%" }}
                    className="beautiful-scrollbar px-5 pt-2"
                  >
                    <NumericalInstructions />
                  </div>
                  <div className="dividing-border" />
                </div>
                <Col xs={6} />
              </>
            ) : (
              <>
                {showCalculator ? (
                  <Col xs={4}>
                    <div style={{ width: "30%" }}></div>
                  </Col>
                ) : (
                  ""
                )}
              </>
            )}
            <Col
              className="px-5 pt-3"
              style={
                currentQuestion?.type === "numerical"
                  ? {}
                  : {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }
              }
              xs={
                currentQuestion?.type === "numerical"
                  ? 6
                  : showCalculator
                    ? 8
                    : 12
              }
            >
              <div
                style={{
                  width:
                    currentQuestion?.type !== "numerical" && !showCalculator
                      ? "40%"
                      : currentQuestion?.type !== "numerical" && showCalculator
                        ? "50%"
                        : "",
                }}
              >
                <ExamOptions
                  isSecondHalf={isSecondHalf}
                  question={currentQuestion}
                  index={currentQuestionIndex}
                  selectedOption={selectedOption}
                  handleAnswerSelect={handleAnswerSelect}
                  markedForLater={markedQuestions?.includes(
                    examData?.sections[currentSectionIndex]?.questions[
                      currentQuestionIndex
                    ]?._id,
                  )}
                  toggleMarkQuestion={toggleMarkQuestion}
                  eliminatedOptions={eliminatedOptions}
                  onOptionEliminate={handleOptionEliminate}
                  resetEliminatedOptions={resetEliminatedOptions}
                  setNumericAnswer={setNumericAnswer}
                  numericAnswer={numericAnswer}
                />
              </div>
            </Col>
          </Row>
        </Container>
      );
    } else {
      // Layout for sections 1
      return (
        <div style={{ paddingTop: "71px" }}>
          <div
            xs={6}
            style={{
              padding: 0,
              display: "flex",
              position: "fixed",
              top: "82px",
              background: "white",
              width: sizes[0],
              zIndex: 1,
            }}
          >
            <div
              style={{ width: "100%" }}
              className="beautiful-scrollbar px-5 mt-3"
            >
              <PreviewStatement
                ref={previewStatementRef}
                contentRef={contentRef}
                isEnglishModule={currentSectionIndex < 2}
                highlights={highlights}
                data={currentQuestion}
                handleMouseUp={handleMouseUp}
              />
            </div>
            <div className="dividing-border" />
            <button
              style={{
                background: "black",
                height: "19px",
                width: "19px",
                position: "absolute",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                right: "-8px",
                top: "30px",
                cursor: "grab",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 640 512"
              >
                <path
                  fill="white"
                  d="M32 64c17.7 0 32 14.3 32 32v320c0 17.7-14.3 32-32 32S0 433.7 0 416V96c0-17.7 14.3-32 32-32m214.6 73.4c12.5 12.5 12.5 32.8 0 45.3L205.3 224h229.5l-41.4-41.4c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l96 96c12.5 12.5 12.5 32.8 0 45.3l-96 96c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l41.3-41.3H205.2l41.4 41.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0l-96-96c-12.5-12.5-12.5-32.8 0-45.3l96-96c12.5-12.5 32.8-12.5 45.3 0M640 96v320c0 17.7-14.3 32-32 32s-32-14.3-32-32V96c0-17.7 14.3-32 32-32s32 14.3 32 32"
                />
              </svg>
            </button>
          </div>
          <div
            style={{
              height: "calc(100vh - 136px)",
              overflowY: "auto",
            }}
          >
            <SplitPane
              split="vertical"
              sizes={sizes}
              onChange={(sizes) => setSizes(sizes)}
            >
              <Pane minSize={50} maxSize="70%">
                <div>
                  <p></p>
                </div>
              </Pane>
              <div
                className=" beautiful-scrollbar px-5 mt-3"
                style={{
                  overflowX: "hidden",
                }}
              >
                <ExamOptions
                  question={currentQuestion}
                  index={currentQuestionIndex}
                  selectedOption={selectedOption}
                  handleAnswerSelect={handleAnswerSelect}
                  isSecondHalf={isSecondHalf}
                  markedForLater={markedQuestions.includes(
                    examData?.sections[currentSectionIndex]?.questions[
                      currentQuestionIndex
                    ]?._id,
                  )}
                  toggleMarkQuestion={toggleMarkQuestion}
                  eliminatedOptions={eliminatedOptions}
                  onOptionEliminate={handleOptionEliminate}
                  resetEliminatedOptions={resetEliminatedOptions}
                  setNumericAnswer={setNumericAnswer}
                  numericAnswer={numericAnswer}
                />
              </div>
            </SplitPane>
          </div>
        </div>
      );
    }
  };

  const renderReviewScreen = () => {
    return (
      <ReviewScreen
        currentSectionIndex={currentSectionIndex}
        sectionQuestions={currentSectionQuestions}
        examData={examData}
        markedQuestions={markedQuestions}
        userAnswers={userAnswers}
        currentQuestionIndex={currentQuestionIndex}
        reviewMode={reviewMode}
        failAttempt={failedAttempt}
        isErrorModalOpen={isErrorModalOpen}
        setIsErrorModalOpen={setIsErrorModalOpen}
      />
    );
  };

  const handleTimerEnd = () => {
    if(failedAttempt) {
      return
    }
    hiddenButton?.current?.click();
  };

  if (isloading) return <Loader />;

  return (
    <div>
      <button
        ref={hiddenButton}
        style={{ display: "none" }}
        onClick={goToNextQuestion.bind(null, true, true)}
      >
        hide button
      </button>
      {openSaveAndExist && (
        <SaveAndExitDialog
          open={openSaveAndExist}
          setOpen={setOpenSaveAndExist}
          onSaveAndExitHandler={handleSaveAndExitHandler}
        />
      )}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: "#e6edf8",
          zIndex: 1,
          marginBottom: "7px",
        }}
      >
        <ExamHeader
          handleAnnotateClick={handleAnnotateClick}
          currentSectionIndex={currentSectionIndex}
          toggleCalculator={toggleCalculator}
          examData={examData}
          handleTimerEnd={handleTimerEnd}
          saveAndExit={onSaveAndExitClick}
          toggleRefernce={toggleRefernce}
          time={ failedAttempt ? '--:--' : time}
        />
        <Calculator
          show={showCalculator}
          onClose={() => setShowCalculator(false)}
        />
        <Reference
          show={showReference}
          onClose={() => setShowReference(false)}
        />
        <DividerLine />
      </div>
      <div style={{ marginBottom: "60px" }}>
        {(reviewMode || failedAttempt) ? renderReviewScreen() : renderQuestionScreen()}
      </div>
      <Footer
        currentQuestionIndex={currentQuestionIndex}
        openReviewModal={openReviewModal}
        reviewModalOpen={reviewModalOpen}
        closeReviewModal={closeReviewModal}
        goToPreviousQuestion={goToPreviousQuestion}
        goToNextQuestion={goToNextQuestion}
        currentSectionIndex={currentSectionIndex}
        examData={examData}
        markedQuestions={markedQuestions}
        userAnswers={userAnswers}
        reviewMode={reviewMode}
        failAttempt={failedAttempt}
      />
      {showAnnotationModal && !failedAttempt && (
        <AnnotationModal
          handleNewAnnotation={handleNewAnnotation}
          selectedText={selectedText}
          showModal={showAnnotationModal}
          handleClose={() => setShowAnnotationModal(false)}
        />
      )}
    </div>
  );
}

export default memo(Exams);
